import { clone, findIndex, get, setWith } from 'lodash';

type Path = string | number | (string | number)[];

export const setPath = <T extends Record<string, any>, U>(src: T, path: Path, value: U): T => {
  return setWith(clone<T>(src), path, value, <V>(field: V) => {
    if (field === null) {
      return {};
    } else {
      return clone(field);
    }
  });
};

export const mapPath = <T extends Record<string, any>, U>(src: T, path: Path, mapper: (v: U) => U) => {
  const oldVal = get(src, path) as U;
  const newVal = mapper(oldVal);
  return setPath(src, path, newVal);
};

export const mapPathLenFn =
  <U>(path: Path, mapper: (v: U) => U) =>
  <T extends Record<string, any>>(src: T) => {
    return mapPath(src, path, mapper);
  };

export const mapArrayElement = <T>(arr: T[], target: number | ((item: T) => boolean), mapper: (v: T) => T) => {
  const index = typeof target === 'number' ? target : findIndex(arr, target);
  if (index === -1) {
    return arr;
  }
  const newVal = mapper(arr[index]);
  return [...arr.slice(0, index), newVal, ...arr.slice(index + 1)];
};

export const mapBulkArrayElements = <T extends { id: string | number }>(
  arr: T[],
  ids: Array<string | number>,
  mapper: (v: T) => T
): T[] => {
  if (!ids.length) return arr;

  let modified = false;
  const newArr = arr.map((item) => {
    if (ids.includes(item.id)) {
      modified = true;
      return mapper(item);
    }
    return item;
  });

  return modified ? newArr : arr;
};

export const mapTree =
  <T>(getChildren: (t: T) => T[] | null, setChildren: (t: T, c: T[]) => T) =>
  (tree: T, mapper: (t: T) => T): T => {
    const newTree = mapper(tree);
    const children = getChildren(newTree);

    if (!children || children.length === 0) {
      return newTree;
    }

    const newChildren = children.map((t) => {
      return mapTree(getChildren, setChildren)(t, mapper);
    });

    return setChildren(newTree, newChildren);
  };

export const mapTreeElement = <T>(getChildren: (t: T) => T[] | null, setChildren: (t: T, c: T[]) => T) => {
  const treeTraverse = mapTree(getChildren, setChildren);
  return (tree: T, targeter: (item: T) => boolean, mapper: (v: T) => T): T => {
    return treeTraverse(tree, (t) => (targeter(t) ? mapper(t) : t));
  };
};

export const reorderArrayElements =
  (source: number, destination: number) =>
  <T>(original: T[]) => {
    const reordered = original.slice();
    const [reorderedItem] = reordered.splice(source, 1);
    reordered.splice(destination, 0, reorderedItem);
    return reordered;
  };
